import { Injectable, WritableSignal, inject, signal } from '@angular/core';
import { Observable, catchError, map, of } from 'rxjs';
import { startWith } from 'rxjs/operators';
import { FindReceiptsResponse, ReceiptsApiServiceObservable } from '@kempoweroyj/ngx-receipts-api-client';
import { ConfigService } from '@kempower-nx-workspace/receipt-lookup-environment';
import { buildSessionStorageKey } from '../utils/session-storage-utils';
import { HttpErrorResponse } from '@angular/common/http';

export interface SearchResult {
    loading: boolean;
    data?: FindReceiptsResponse;
    error?: string;
    errorStatus?: number;
    errorObject?: HttpErrorResponse;
}

const STORAGE_KEYS = {
    RECEIPT_SEARCH_RESULTS: 'receipt_search_results',
    SEARCH_JUST_COMPLETED: 'search_just_completed',
} as const;

@Injectable({
    providedIn: 'root',
})
export class ReceiptSearchService {
    readonly receiptsApiService$: ReceiptsApiServiceObservable = inject(ReceiptsApiServiceObservable);
    readonly configService$: ConfigService = inject(ConfigService);

    readonly #SEARCH_RESULTS_KEY = buildSessionStorageKey(
        this.configService$.extractedCustomerId,
        STORAGE_KEYS.RECEIPT_SEARCH_RESULTS
    );
    readonly #SEARCH_JUST_COMPLETED_KEY = buildSessionStorageKey(
        this.configService$.extractedCustomerId,
        STORAGE_KEYS.SEARCH_JUST_COMPLETED
    );
    readonly #LOCAL_STORAGE_EXPIRATION_MS = 3600000;

    readonly currentResults: WritableSignal<SearchResult> = signal<SearchResult>({
        loading: false,
        data: undefined,
        error: undefined,
    });

    static readonly STORAGE_KEYS = STORAGE_KEYS;

    private getCookie(name: string): string | null {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) {
            return parts.pop()?.split(';').shift() || null;
        }
        return null;
    }

    searchReceipts$(customerId: string, date: string, lastFourDigits: string): Observable<SearchResult> {
        // Clear old stored results when starting a new search
        this.clearStoredResults();

        const awsWafToken = this.getCookie('aws-waf-token');

        const headers: { [key: string]: string } = {};
        if (awsWafToken) {
            headers['x-aws-waf-token'] = awsWafToken;
        }
        return this.receiptsApiService$.findReceipts(customerId, { date, lastFourDigits }, { headers }).pipe(
            map(
                (response: FindReceiptsResponse): SearchResult => ({
                    loading: false,
                    data: response,
                    error: undefined,
                })
            ),
            startWith({
                loading: true,
                data: undefined,
                error: undefined,
            } as SearchResult),
            catchError((error: unknown): Observable<SearchResult> => {
                const errorObj = error as any;
                return of({
                    loading: false,
                    data: undefined,
                    error: errorObj?.message || 'An unexpected error occurred',
                    errorObject: errorObj as HttpErrorResponse,
                    errorStatus: errorObj?.status || errorObj?.statusCode || 500,
                });
            })
        );
    }

    getCurrentResults(): SearchResult {
        return this.currentResults();
    }

    // Method for controller to update state and persist results
    updateResults(result: SearchResult): void {
        this.currentResults.set(result);
        this.persistResults(result);
    }

    hasValidResults(): boolean {
        const current: SearchResult = this.getCurrentResults();

        // Allow access if we have valid current results (with actual data)
        if (current.data && current.data.length > 0 && !current.loading && !current.error) {
            return true;
        }
        try {
            const stored = localStorage.getItem(this.#SEARCH_RESULTS_KEY);
            if (stored) {
                const parsed = JSON.parse(stored);
                // Check if data is expired (older than 1 hour)
                if (parsed.savedAt && Date.now() - parsed.savedAt > this.#LOCAL_STORAGE_EXPIRATION_MS) {
                    this.clearStoredResults();
                    return false;
                }
                return !!(parsed.data && parsed.data.length > 0);
            }
        } catch (error) {
            console.error('Error checking stored results: ', error);
        }

        return false;
    }

    persistResults(result: SearchResult): void {
        // Only persist non-empty results in localStorage
        if (result.data && result.data.length > 0 && !result.error) {
            try {
                const payload = {
                    ...result,
                    savedAt: Date.now(),
                };
                localStorage.setItem(this.#SEARCH_RESULTS_KEY, JSON.stringify(payload));
            } catch (error) {
                console.error('Error persisting results: ', error);
            }
        }

        if (!result.error) {
            sessionStorage.setItem(this.#SEARCH_JUST_COMPLETED_KEY, 'true');
        }
    }

    /**
     * Loads persisted search results from localStorage
     * We return cached results newer than 60 minutes and remove results older than that from local storage
     * @returns SearchResult object if valid cached results exist, null otherwise
     */
    loadPersistedResults(): SearchResult | null {
        try {
            const stored: string | null = localStorage.getItem(this.#SEARCH_RESULTS_KEY);
            if (!stored) return null;

            const parsed = JSON.parse(stored);

            if (parsed.savedAt && Date.now() - parsed.savedAt > this.#LOCAL_STORAGE_EXPIRATION_MS) {
                this.clearStoredResults();
                return null;
            }

            const { savedAt, ...result } = parsed;
            return result;
        } catch (error) {
            console.error('Error loading persisted results: ', error);
            return null;
        }
    }

    /**
     * Clears all stored search results from localStorage and sessionStorage
     */
    clearStoredResults(): void {
        try {
            localStorage.removeItem(this.#SEARCH_RESULTS_KEY);
            sessionStorage.removeItem(this.#SEARCH_JUST_COMPLETED_KEY);
        } catch (error) {
            console.error('Error clearing stored results: ', error);
        }
    }

    // Method for testing purposes - resets the service state
    resetServiceState(): void {
        this.currentResults.set({ loading: false, data: [] });
        this.clearStoredResults();
    }
}
